import { authorized } from "../authorized";
import { env } from "../env";
import { AccountRegion } from "../types/regionalApi";
import {
  FetchCAMAccountResult,
  FetchTwoFAResult,
  ForgotUsernameResult,
  SendGridClientInterface,
  SendGridClientOptions,
} from "./types";

interface APIError {
  field?: string;
  message?: string;
  help?: string;
}

interface APIResponse {
  errors?: APIError[];
  status: number;
}

interface FetchTwoFAResponse extends APIResponse {
  is_verified?: boolean;
}

interface FetchCAMAccountResponse extends APIResponse {
  catalog_org?: string;
}

interface ForgotUsernameResponse {
  errors?: Array<{ error_id: string; message: string }>;
}

/**
 * This is an adaptor used to call SendGrid based functions.
 */
export class SendGridClient implements SendGridClientInterface {
  // Properties
  private readonly options: SendGridClientOptions;
  private readonly baseURL: string;

  private readonly defaultOptions: Partial<SendGridClientOptions> = {
    baseURL: env.VITE_API_BASE_URL || "https://api.staging.sendgrid.com",
  };

  // Constructor provide the base url.
  constructor(options: SendGridClientOptions) {
    this.options = {
      ...this.defaultOptions,
      ...options,
    };
    this.baseURL = getBaseURL(this.options.baseURL || "https://api.sendgrid.com");
  }

  async FetchTwoFASetting(token: string, region: AccountRegion): Promise<FetchTwoFAResult> {
    const fetch = authorized({ token, region });
    try {
      const response = await fetch(`${this.baseURL}/v3/public/access_settings/multifactor`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!response) {
        return { success: false };
      }
      if (response.status === 404) {
        return {
          success: true,
          is_verified: false,
        };
      }
      let body: FetchTwoFAResponse | undefined;
      body = (await response.json()) as FetchTwoFAResponse;
      if (response.status !== 200 && response.status !== 404) {
        return {
          success: false,
        };
      }
      return {
        success: true,
        is_verified: body?.is_verified ?? false,
      };
    } catch (e) {
      return { success: false };
    }
  }

  async fetchCAMAccount(token: string, region: AccountRegion): Promise<FetchCAMAccountResult> {
    const fetch = authorized({ token, region });
    try {
      const response = await fetch(`${this.baseURL}/v3/accounts`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!response) {
        return { success: false };
      }
      let body: FetchCAMAccountResponse | undefined;
      body = (await response.json()) as FetchCAMAccountResponse;
      if (response.status !== 200) {
        return {
          success: false,
        };
      }
      return {
        success: true,
        catalog_org: body?.catalog_org,
      };
    } catch (e) {
      return { success: false };
    }
  }

  async forgotUsername(email: string): Promise<ForgotUsernameResult> {
    try {
      const response = await fetch(`${this.baseURL}/v3/public/forgot_username`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (response.status === 204) {
        return { success: true };
      }

      let body: ForgotUsernameResponse | undefined;
      try {
        body = (await response.json()) as ForgotUsernameResponse;
      } catch (e) {
        return { success: false, status: response.status };
      }

      return {
        success: false,
        status: response.status,
        error_id: body?.errors?.[0]?.error_id,
        message: body?.errors?.[0]?.message,
      };
    } catch (e) {
      return {
        success: false,
        status: 0,
        message: e instanceof Error ? e.message : "Network error",
      };
    }
  }
}

/**
 * @ignore
 */
const getBaseURL = (baseURL: string) => {
  if (!/^https?:\/\//i.test(baseURL)) {
    return `https://${baseURL}`;
  }

  return baseURL;
};
